import ReactMarkdown from "react-markdown";
import { useRouter } from "next/router";
import WButton from "../wbutton/WButton";
import clsx from "clsx";
import LCountDownTimer from "../lcount-down-timer/LCountDownTimer";
import { motion } from "framer-motion";
import { ReactNode } from "react";
import parse from "html-react-parser";
import fbEvent from "@/utils/fbEvent";
import { Enum_Componentcmsdatatextandimagesection_Variation } from "@/generated";
import { CornerImage } from "../CornerImage";
import Image from "next/image";

/* eslint-disable-next-line */
export interface LTextImageSectionProps {
  id?: string;
  topTitle?: string;
  topTitleColor?: string;
  mainTitle: string;
  mainTitleColor?: string;
  textContent: string; //rich text
  textContentColor?: string;
  buttons?: {
    text: string;
    url: string;
    color?: string;
    textColor?: string;
  }[];
  imageMask: {
    url: string;
    altTag: string;
  };
  backgroundImageMask: {
    url: string;
    altTag: string;
  };
  underTextImage?: {
    url: string;
    altTag: string;
  };
  hideImageOnMobile?: boolean;
  reverse?: boolean;
  countDownTimer?: {
    endDate: Date;
    description?: string;
  };
  maskImage?: boolean;
  children?: ReactNode;
  bigImageOnMobile?: boolean;
  variation?: Enum_Componentcmsdatatextandimagesection_Variation;
  rounded?: boolean;
  shadow?: boolean;
  backgroundColor?: string;
}

export function LTextImageSection({
  children,
  ...props
}: LTextImageSectionProps) {
  const router = useRouter();

  return (
    <div
      id={props?.id}
      style={{
        backgroundColor: props?.backgroundColor,
      }}
      className={clsx(
        props?.variation ===
          Enum_Componentcmsdatatextandimagesection_Variation.TopLeft ||
          props?.variation ===
            Enum_Componentcmsdatatextandimagesection_Variation.TopRight
          ? "py-10 lg:py-20"
          : "py-10",
        props?.backgroundColor ? "" : "bg-eklight"
      )}
    >
      <div
        className={clsx([
          props?.reverse
            ? "flex-col-reverse lg:flex-row-reverse"
            : "flex-col lg:flex-row",
          "container flex  items-center px-4 mx-auto lg:max-w-7xl",
          props?.variation ===
            Enum_Componentcmsdatatextandimagesection_Variation.BottomLeft ||
          props?.variation ===
            Enum_Componentcmsdatatextandimagesection_Variation.BottomRight
            ? "gap-20"
            : "gap-10",
        ])}
      >
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          transition={{ duration: 1, type: "spring", delay: 0.1 }}
          className="flex flex-col order-last w-full gap-5 lg:w-[40%] lg:order-none"
        >
          <div
            className="-mb-4 text-xl font-semibold text-blue"
            style={{
              color: props?.topTitleColor,
            }}
          >
            {props?.topTitle}
          </div>
          <h1 style={{ color: props?.mainTitleColor }}>
            {parse(props?.mainTitle)}
          </h1>
          <div
            className="flex flex-col gap-5"
            style={{ color: props?.textContentColor }}
          >
            <ReactMarkdown
              // disable es-lint because react-markedown decided its a "good" idea to call the prop "children"
              // eslint-disable-next-line react/no-children-prop
              children={props?.textContent}
            />
            {children}
          </div>
          {props?.countDownTimer?.endDate && (
            <div>
              <LCountDownTimer
                endDate={new Date(props?.countDownTimer?.endDate)}
                description={props?.countDownTimer?.description}
              />
            </div>
          )}
          {props?.buttons && (
            <div>
              {props?.buttons?.map((button, idx) => (
                <WButton
                  key={idx}
                  text={button?.text}
                  color={button?.color}
                  textColor={button?.textColor}
                  type="primary-red-btn"
                  className="mx-auto w-fit lg:mx-0"
                  onClick={() => {
                    button?.url && router.push(button?.url);
                    fbEvent("Schedule");
                  }}
                />
              ))}
            </div>
          )}

          {props?.underTextImage?.url && (
            <div className="flex items-center justify-center w-full">
              <Image
                src={props?.underTextImage?.url}
                alt={props?.underTextImage?.altTag || "image"}
                width={500}
                height={300}
                quality={80}
              />
            </div>
          )}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          transition={{ duration: 1, type: "spring", delay: 0.3 }}
          className={clsx([
            props?.hideImageOnMobile && "hidden lg:block",
            props?.bigImageOnMobile ? "w-full" : "lg:w-1/2",
            props?.maskImage && "mask-bg",
            "lg:w-[60%]",
            "order-first justify-center align-center flex lg:order-none",
          ])}
        >
          <CornerImage
            mainImage={props?.imageMask?.url}
            backgroundImage={props?.backgroundImageMask?.url}
            variation={props?.variation}
            rounded={props?.rounded}
            shadow={props?.shadow}
          />
        </motion.div>
      </div>
    </div>
  );
}

export default LTextImageSection;
